/**
 * RoundForm Component
 *
 * Column 2 of CustomRoundModal
 * Contains the main form for round configuration including:
 * - Round title
 * - Question type
 * - Question generation prompt
 * - Evaluation criteria
 * - Scoring & qualification settings
 * - Generate Sample Questions button (moved from Column 3)
 */

import React, { useMemo } from "react";
import { 
  Form, 
  Input, 
  Radio, 
  Typography, 
  Space,
  Slider,
  InputNumber,
  Switch,
  Row,
  Col,
  Button,
  Alert,
  Skeleton,
} from "antd";
import {
  FormOutlined,
  TrophyOutlined,
  QuestionCircleOutlined,
  ExperimentOutlined,
} from "@ant-design/icons";
import { RoundFormProps } from "../types";
import { SHARED_STYLES, COLUMN_STYLES, FORM_SECTION_STYLES, ColumnStatus } from "../constants";
import ColumnProgressIndicator from "./ColumnProgressIndicator";
import { 
  DEFAULT_PASS_PERCENTAGE, 
  MIN_PASS_PERCENTAGE, 
  MAX_PASS_PERCENTAGE 
} from "../../../../../utils/constants";

const { TextArea } = Input;
const { Text } = Typography;

const RoundForm: React.FC<RoundFormProps> = ({
  form,
  formValues,
  settingsOnlyMode,
  shouldShowEvaluationCriteria,
  isFormFilled,
  isEditing,
  isGeneratingTemplate,
  onQuestionTypeChange,
  
  // Sample questions generation (moved from Column 3)
  hasRequirements,
  hasSampleQuestions,
  isSampleQuestionsLoading,
  onGenerateSampleQuestions,
}) => {
  // Determine if generate button should be disabled
  const isGenerateDisabled = !isFormFilled || !hasRequirements;

  // Compute column progress status
  const columnStatus: ColumnStatus = useMemo(() => {
    // Completed when form is fully filled
    if (isFormFilled) return "completed";
    // In progress when at least one field has content
    const title = formValues?.title?.trim();
    const prompt = formValues?.questionGenerationPrompt?.trim();
    if (title || prompt) return "inProgress";
    // Todo when form is empty
    return "todo";
  }, [isFormFilled, formValues]);

  return (
    <div
      style={{
        ...COLUMN_STYLES.base,
        ...COLUMN_STYLES.column2,
      }}
    >
      {/* Header with Progress Indicator */}
      <div style={COLUMN_STYLES.headerWrapper}>
        <div style={COLUMN_STYLES.header}>
          <Text strong style={{ fontSize: 16 }}>
            <FormOutlined style={{ marginRight: 8, color: SHARED_STYLES.primaryColor }} />
            Round Configuration
          </Text>
          <Text type="secondary" style={{ display: "block", fontSize: SHARED_STYLES.fontSizeSm }}>
            {settingsOnlyMode 
              ? "Modify round settings" 
              : "Configure details and AI prompts"}
          </Text>
        </div>
        <ColumnProgressIndicator status={settingsOnlyMode ? "completed" : columnStatus} />
      </div>

      {/* Scrollable Content */}
      <div
        className="custom-round-modal-scrollable"
        style={COLUMN_STYLES.scrollableContent}
      >
        {settingsOnlyMode && (
          <Alert
            type="info"
            message="Settings Mode"
            description="You can modify the scoring and qualification settings for this predefined round."
            showIcon
            style={{ marginBottom: SHARED_STYLES.marginMd }}
          />
        )}

        {isGeneratingTemplate ? (
          // Skeleton loading state while generating template
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            {/* Skeleton for Round Details */}
            <div style={FORM_SECTION_STYLES.container}>
              <Skeleton.Input active size="small" style={{ width: 100, marginBottom: 12 }} />
              <Skeleton.Input active style={{ width: "100%", marginBottom: 16, height: 32 }} />
              <Skeleton.Input active size="small" style={{ width: 80, marginBottom: 8 }} />
              <Skeleton.Button active style={{ width: 280, height: 32 }} />
            </div>

            {/* Skeleton for AI Configuration */}
            <div style={FORM_SECTION_STYLES.container}>
              <Skeleton.Input active size="small" style={{ width: 120, marginBottom: 12 }} />
              <Skeleton.Input active size="small" style={{ width: 180, marginBottom: 8 }} />
              <Skeleton.Input active style={{ width: "100%", height: 120, marginBottom: 16 }} />
              <Skeleton.Input active size="small" style={{ width: 140, marginBottom: 8 }} />
              <Skeleton.Input active style={{ width: "100%", height: 96 }} />
            </div>

            {/* Skeleton for Scoring & Qualification */}
            <div style={FORM_SECTION_STYLES.lastContainer}>
              <Skeleton.Input active size="small" style={{ width: 160, marginBottom: 12 }} />
              <Row gutter={16}>
                <Col xs={24} sm={24} md={10}>
                  <Space align="start">
                    <Skeleton.Avatar active size="small" shape="square" style={{ width: 40, height: 22, borderRadius: 11 }} />
                    <div>
                      <Skeleton.Input active size="small" style={{ width: 100, marginBottom: 4 }} />
                      <Skeleton.Input active size="small" style={{ width: 130, height: 14 }} />
                    </div>
                  </Space>
                </Col>
                <Col xs={24} sm={24} md={14}>
                  <Skeleton.Input active size="small" style={{ width: 110, marginBottom: 8 }} />
                  <Skeleton.Input active style={{ width: "100%", height: 32, marginBottom: 8 }} />
                  <Skeleton.Input active style={{ width: "100%", height: 32 }} />
                </Col>
              </Row>
            </div>
          </Space>
        ) : (
          <Form
            form={form}
            layout="vertical"
            autoComplete="off"
            requiredMark="optional"
          >
            {/* Group 1: Round Identification */}
            <div style={FORM_SECTION_STYLES.container}>
              <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
                Round Details
              </Text>
              
              <Form.Item
                name="title"
                label="Round Title"
                rules={[
                  { required: true, message: "Please enter a round title" },
                  { min: 3, message: "Title must be at least 3 characters" },
                ]}
              >
                <Input
                  placeholder="e.g., Technical Assessment, Problem Solving Round"
                  maxLength={100}
                  showCount
                  disabled={settingsOnlyMode}
                />
              </Form.Item>

              <Form.Item
                name="questionType"
                label="Question Type"
                rules={[{ required: true, message: "Please select a question type" }]}
                initialValue="mixed"
                style={{ marginBottom: 0 }}
              >
                <Radio.Group
                  disabled={settingsOnlyMode}
                  buttonStyle="solid"
                  optionType="button"
                  onChange={(e) => {
                    // Sync to Column 1 when user changes Question Type in Column 2
                    onQuestionTypeChange?.(e.target.value);
                  }}
                >
                  <Radio.Button value="mcq">MCQ</Radio.Button>
                  <Radio.Button value="descriptive">Descriptive</Radio.Button>
                  <Radio.Button value="coding">Coding</Radio.Button>
                  <Radio.Button value="mixed">Mixed</Radio.Button>
                  {/* <Radio.Button value="simulation">Simulation</Radio.Button> */}
                </Radio.Group>
              </Form.Item>
            </div>

            {/* Simulation info banner */}
            {formValues?.questionType === "simulation" && (
              <div style={FORM_SECTION_STYLES.container}>
                <Alert
                  type="info"
                  message="Simulation Round"
                  description="This round uses an AI-powered simulation to assess candidates in a realistic scenario. A simulation blueprint will be automatically configured when the job post is published."
                  showIcon
                  icon={<ExperimentOutlined />}
                />
              </div>
            )}

            {/* Group 2: AI Prompts - Hidden for simulation rounds */}
            {formValues?.questionType !== "simulation" && (
            <div style={FORM_SECTION_STYLES.container}>
              <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
                AI Configuration
              </Text>

              <Form.Item
                name="questionGenerationPrompt"
                label="Question Generation Prompt"
                rules={[
                  { required: formValues?.questionType !== "simulation", message: "Please enter a question generation prompt" },
                  { min: 20, message: "Prompt must be at least 20 characters for better results" },
                ]}
                tooltip="This prompt guides AI to generate relevant interview questions"
              >
                <TextArea
                  placeholder="Describe the type of questions you want AI to generate. Be specific about skills, difficulty level, and focus areas."
                  rows={5}
                  showCount
                  disabled={settingsOnlyMode}
                />
              </Form.Item>

              {/* Always render to preserve form value, but hide visually when not applicable */}
              <Form.Item
                name="evaluationCriteria"
                label="Evaluation Criteria"
                tooltip={shouldShowEvaluationCriteria ? "This prompt guides AI to evaluate the candidate's answer" : undefined}
                preserve={true}
                initialValue=""
                style={{
                  marginBottom: 0,
                  display: shouldShowEvaluationCriteria ? "block" : "none"
                }}
              >
                <TextArea
                  placeholder="Describe how candidate responses should be evaluated."
                  rows={4}
                  showCount
                  disabled={settingsOnlyMode}
                />
              </Form.Item>
            </div>
            )}

          {/* Group 3: Scoring & Qualification */}
          <div style={FORM_SECTION_STYLES.lastContainer}>
            <Text
              type="secondary"
              style={{ display: "block", marginBottom: 12 }}
            >
              <TrophyOutlined style={{ marginRight: 6 }} />
              Scoring & Qualification
            </Text>

            <Row gutter={16}>
              <Col xs={24} sm={24} md={10}>
                <Form.Item
                  name="isMustQualify"
                  valuePropName="checked"
                  initialValue={true}
                  style={{ marginBottom: 0 }}
                >
                  <Space align="start" style={{ width: "100%" }}>
                    <Switch
                      checked={formValues?.isMustQualify ?? true}
                      onChange={(checked) =>
                        form.setFieldValue("isMustQualify", checked)
                      }
                    />
                    <div>
                      <Text>Eliminator Round</Text>
                      <br />
                      <Text
                        type="secondary"
                        style={{ fontSize: SHARED_STYLES.fontSizeSm }}
                      >
                        {formValues?.isMustQualify ?? true
                          ? "Must qualify to proceed"
                          : "Can proceed regardless"}
                      </Text>
                    </div>
                  </Space>
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={14}>
                <Form.Item
                  name="passPercentage"
                  label="Pass Percentage"
                  tooltip="Minimum score required to pass this round"
                  style={{ marginBottom: 0 }}
                  validateStatus={
                    formValues?.passPercentage !== undefined &&
                    formValues?.passPercentage !== null &&
                    (formValues.passPercentage < MIN_PASS_PERCENTAGE ||
                      formValues.passPercentage > MAX_PASS_PERCENTAGE)
                      ? "error"
                      : undefined
                  }
                  help={
                    formValues?.passPercentage !== undefined &&
                    formValues?.passPercentage !== null &&
                    (formValues.passPercentage < MIN_PASS_PERCENTAGE ||
                      formValues.passPercentage > MAX_PASS_PERCENTAGE)
                      ? `Must be between ${MIN_PASS_PERCENTAGE}% and ${MAX_PASS_PERCENTAGE}%`
                      : undefined
                  }
                >
                  <Space
                    direction="vertical"
                    style={{ width: "100%" }}
                    size="small"
                  >
                    <Slider
                      min={MIN_PASS_PERCENTAGE}
                      max={MAX_PASS_PERCENTAGE}
                      value={
                        formValues?.passPercentage ?? DEFAULT_PASS_PERCENTAGE
                      }
                      onChange={(value) =>
                        form.setFieldValue("passPercentage", value)
                      }
                      tooltip={{ formatter: (value) => `${value}%` }}
                    />
                    <InputNumber
                      min={MIN_PASS_PERCENTAGE}
                      max={MAX_PASS_PERCENTAGE}
                      precision={0}
                      controls={true}
                      value={
                        formValues?.passPercentage ?? DEFAULT_PASS_PERCENTAGE
                      }
                      status={
                        formValues?.passPercentage !== undefined &&
                        formValues?.passPercentage !== null &&
                        (formValues.passPercentage < MIN_PASS_PERCENTAGE ||
                          formValues.passPercentage > MAX_PASS_PERCENTAGE)
                          ? "error"
                          : undefined
                      }
                      onChange={(value) => {
                        if (value === null || value === undefined) {
                          form.setFieldValue(
                            "passPercentage",
                            MIN_PASS_PERCENTAGE
                          );
                          return;
                        }
                        // Validate: must be between MIN and MAX
                        form.setFieldValue("passPercentage", value);
                      }}
                      onKeyDown={(e) => {
                        const allowedKeys = [
                          "Backspace",
                          "Delete",
                          "Tab",
                          "Escape",
                          "Enter",
                          "ArrowLeft",
                          "ArrowRight",
                          "ArrowUp",
                          "ArrowDown",
                          "Home",
                          "End",
                        ];
                        if (allowedKeys.includes(e.key)) {
                          return;
                        }
                        if (
                          e.ctrlKey &&
                          ["a", "c", "x"].includes(e.key.toLowerCase())
                        ) {
                          return;
                        }
                        if (e.ctrlKey && e.key.toLowerCase() === "v") {
                          e.preventDefault();
                          return;
                        }
                        if (!/^[0-9]$/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      // onPaste={(e) => {
                      //   e.preventDefault();
                      //   const pastedText = e.clipboardData.getData("text");
                      //   const numbersOnly = pastedText.replace(/[^0-9]/g, "");
                      //   if (numbersOnly) {
                      //     const parsed = Number.parseInt(numbersOnly, 10);
                      //     form.setFieldValue("passPercentage", parsed);
                      //   }
                      // }}
                      onBlur={() => {
                        // Clamp value on blur to valid range
                        const currentValue =
                          formValues?.passPercentage ?? DEFAULT_PASS_PERCENTAGE;
                        const clamped = Math.max(
                          MIN_PASS_PERCENTAGE,
                          Math.min(MAX_PASS_PERCENTAGE, currentValue)
                        );
                        form.setFieldValue("passPercentage", clamped);
                      }}
                      addonAfter={<span style={{ userSelect: "none" }}>%</span>}
                      style={{ width: "100%" }}
                    />
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </div>
        </Form>
        )}
      </div>

      {/* Footer - Generate Sample Questions (hidden for simulation rounds) */}
      {formValues?.questionType !== "simulation" && (
      <div
        style={{
          ...COLUMN_STYLES.footer,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <Button
          type="default"
          size="large"
          icon={<QuestionCircleOutlined />}
          onClick={onGenerateSampleQuestions}
          loading={isSampleQuestionsLoading}
          disabled={isGenerateDisabled}
          style={{ width: "100%" }}
        >
          {isSampleQuestionsLoading
            ? "Generating..."
            : hasSampleQuestions
            ? "Regenerate Sample Questions"
            : "Generate Sample Questions"}
        </Button>
        {isGenerateDisabled && !settingsOnlyMode && (
          <Text
            type="danger"
            style={{ fontSize: SHARED_STYLES.fontSizeSm, textAlign: "center" }}
          >
            {!isFormFilled
              ? "Fill in Round Title and Question Prompt first"
              : "Add requirements in Step 2 first"}
          </Text>
        )}
      </div>
      )}
    </div>
  );
};

export default RoundForm;
